import { DocumentCommonSettingModel } from './document-common-setting.model';
import { DocumentContentModel } from './document-content.model';
import { DocumentControlContentLanguageLabelModel } from './document-control-content-language-label.model';
import { IPublicationSetting, ISiteDesignAccessItem, PublicationSettingModel } from '@fi-shared/models';
import { AccessRightFilterCollectionModel } from '@fi-shared/models';
import { ContentLanguageLabelModel } from '@fi-shared/models';
import { TemplateModel } from '../template/template.model';
import { FiDateFormatOptions } from '@fi-shared/utils/format-date';
import { cloneDeep } from '@fi-shared/utils';
import { IReactionModel } from '@fi-shared/fi-likes/fi-likes';
import { Injector } from '@angular/core';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { FiCustomerDataService } from '@fi-shared/fi-shared-services/fi-customer-data.service';
import { PersonalNotificationsCategoryType } from '@fi-shared/types';
import { INotificationCategoryItem } from '@fi-shared/fi-notification/fi-notification';
import { IDocumentModel, IDocumentVersionModel } from '@fi-app/document/document';
import { FiFormatDateService } from '@fi-shared/fi-format-date/fi-format-date.service';
import { IPublicationItemBase } from '@fi-shared/models/models';

const ContainsContentLanguageId = (list, contentLanguageId, obj) => {
    let i = list.length;
    while (i--) {
        if (list[i].ContentLanguageId === contentLanguageId) {
            obj.Result = list[i];
            return true;
        }
    }
    return false;
};

export class DocumentModel implements INotificationCategoryItem, ISiteDesignAccessItem, IPublicationItemBase {
    Id = null;
    Name = '';
    Description = '';
    TemplateId = null;
    IsFavorite = false;
    IsDeleted = false;
    IsPublished = false;
    IsActivated = true;
    CreatedByUserId = null;
    CreatedByUserName = '';
    CreatedOn = new Date();
    CreatedOnDate = '';
    TagIds = [];
    Tags = [];
    Names: Array<ContentLanguageLabelModel> = [];
    Descriptions: Array<ContentLanguageLabelModel> = [];
    ChangedByUserId = null;
    ChangedByUserName = '';
    ChangedOn = new Date();
    ChangedOnDate = '';
    AccessRightFilterCollection = new AccessRightFilterCollectionModel();
    PublicationSetting: IPublicationSetting = PublicationSettingModel.getNewInstance();
    DocumentSetting: DocumentCommonSettingModel; // = new DocumentCommonSettingModel();
    DocumentVersions: IDocumentVersionModel[] = [];
    DocumentPublishedContents = [];
    MediaBankFileReferences = [];
    DocumentControlContents: DocumentControlContentLanguageLabelModel[] = [];
    PreviewFileName = null;
    Template: TemplateModel = null;
    //this.CommentCount = 0;
    IsDraft = false;
    OriginalDocumentVersions: IDocumentVersionModel[] = [];
    DraftVersion = null;
    DraftNumber = null;
    NotificationCategory = PersonalNotificationsCategoryType.Visited;
    OriginalId = undefined;

    SortOrder: any;
    ObjectType: number;
    Reactions: IReactionModel[];
    SiteDesignIds: number[] = [];

    MediaBankFileIds: {
        [key: number]: boolean;
    };

    HasViewAccessAsSuperAdmin: boolean;

    PublicationStartDate: string;
    PublicationSettingChangedByUserId: number;
    PublicationSettingChangedByUserName: string;

    // https://chainformation.atlassian.net/browse/FI-4935
    GeneratePreviewFileImage?: boolean;

    constructor(data: IDocumentModel, isLightWeightObject, injector: Injector) {
        const customerDataService = injector.get(FiCustomerDataService);
        this.DocumentSetting = new DocumentCommonSettingModel(customerDataService.customerData);
        this.CreatedOnDate = injector.get(FiFormatDateService).formatDate(this.CreatedOn, FiDateFormatOptions);
        this.ChangedOnDate = injector.get(FiFormatDateService).formatDate(this.ChangedOn, FiDateFormatOptions);

        if (data) this.Initialize(data, isLightWeightObject, injector);
    }

    Initialize(document: IDocumentModel, isLightWeightObject, injector: Injector) {
        if (document) {
            this.Id = document.Id;
            this.TemplateId = document.TemplateId;
            this.IsDeleted = document.IsDeleted;
            this.IsPublished = document.IsPublished;
            this.IsActivated = document.IsActivated;
            this.IsFavorite = document.IsFavorite;
            this.SortOrder = document.SortOrder;
            this.CreatedByUserId = document.CreatedByUserId;
            this.CreatedByUserName = document.CreatedByUserName;
            this.CreatedOn = document.CreatedOn;
            this.CreatedOnDate = injector.get(FiFormatDateService).formatDate(this.CreatedOn, FiDateFormatOptions);
            this.ChangedByUserId = document.ChangedByUserId;
            this.ChangedByUserName = document.ChangedByUserName;
            this.ChangedOn = typeof document.ChangedOn === 'string' ? new Date(document.ChangedOn) : document.ChangedOn;
            this.ChangedOnDate = injector.get(FiFormatDateService).formatDate(this.ChangedOn, FiDateFormatOptions);

            this.PublicationStartDate = document.PublicationStartDate;
            this.PublicationSettingChangedByUserId = document.PublicationSettingChangedByUserId;
            this.PublicationSettingChangedByUserName = document.PublicationSettingChangedByUserName;

            this.TagIds = document.TagIds;
            this.Tags = document.Tags || [];
            this.Name = document.Name;
            this.PreviewFileName = document.PreviewFileName;
            this.IsDraft = document.IsDraft || false;
            this.OriginalDocumentVersions = document.OriginalDocumentVersions || [];
            this.DraftVersion = document.DraftVersion;
            this.DraftNumber = document.DraftNumber;
            this.NotificationCategory = document.NotificationCategory;
            this.HasViewAccessAsSuperAdmin = document.HasViewAccessAsSuperAdmin;
            this.OriginalId = document.OriginalId;
            this.Reactions = document.Reactions;
            this.GeneratePreviewFileImage = document.GeneratePreviewFileImage;

            this.SiteDesignIds = document.SiteDesignIds;

            this.PublicationSetting = document.PublicationSetting;

            if (document.Template) {
                this.Template = new TemplateModel(cloneDeep(document.Template), injector);
            }

            //this.CommentCount = document.CommentCount || 0;

            if (!isLightWeightObject) {
                if (document.DocumentSetting != null) this.DocumentSetting.Initialize(document.DocumentSetting);

                if (document.AccessRightFilterCollection != null)
                    this.AccessRightFilterCollection.InitializeAccessRightFilterCollection(
                        document.AccessRightFilterCollection,
                    );

                document.DocumentVersions.forEach((documentVersion) => {
                    const documentPublishedVersion = new DocumentContentModel();
                    const userDataService = injector.get(FiUserDataService);
                    documentPublishedVersion.Initialize(
                        documentVersion,
                        userDataService.userData.PreferredContentLanguage,
                        injector,
                    );
                    this.DocumentVersions.push(documentPublishedVersion);
                });

                this.MediaBankFileReferences = document.MediaBankFileReferences;

                document.DocumentControlContents.forEach((DocumentControlContentsData) => {
                    this.DocumentControlContents.push(
                        new DocumentControlContentLanguageLabelModel(DocumentControlContentsData),
                    );
                });
            }
        }

        const customerDataService = injector.get(FiCustomerDataService);
        this.InitializeDocumentContentLanguages(customerDataService.customerData.ContentLanguages, document);
        this.InitializeDocumentPublishedContents(customerDataService.customerData.ContentLanguages, document, injector);
    }

    InitializeDocumentContentLanguages(contentLanguages, response) {
        const self = this;

        for (let i = 0; i < contentLanguages.length; i++) {
            const contentLanguageId = contentLanguages[i].Id;
            const matchFound = { Result: null };

            if (response && response.Names && ContainsContentLanguageId(response.Names, contentLanguageId, matchFound))
                self.Names.push(
                    new ContentLanguageLabelModel(
                        contentLanguageId,
                        matchFound.Result.Value,
                        matchFound.Result.SortIndex,
                    ),
                );
            else self.Names.push(new ContentLanguageLabelModel(contentLanguageId, '', contentLanguages[i].SortIndex));
        }
    }

    InitializeDocumentPublishedContents(contentLanguages, response, injector: Injector) {
        const self = this;
        for (let i = 0; i < contentLanguages.length; i++) {
            const contentLanguageId = contentLanguages[i].Id;
            const matchFound = { Result: null };
            const documentContent = new DocumentContentModel();

            if (
                response &&
                response.DocumentPublishedContents != null &&
                ContainsContentLanguageId(response.DocumentPublishedContents, contentLanguageId, matchFound)
            ) {
                const userDataService = injector.get(FiUserDataService);
                documentContent.Initialize(
                    matchFound.Result,
                    userDataService.userData.PreferredContentLanguage,
                    injector,
                );
                self.DocumentPublishedContents.push(documentContent);
            } else {
                documentContent.ContentLanguageId = contentLanguageId;
                documentContent.DocumentId = self.Id;
                self.DocumentPublishedContents.push(documentContent);
            }
        }
    }
}
